import { useWallet } from "@/hooks/useWallet";
import { useDashboardData } from "@/hooks/useDashboardData";
import { usePerpsFunding } from "@/hooks/usePerpsFunding";
import { FundingChart } from "@/components/perps/FundingChart";
import { FundingEventsTable } from "@/components/perps/FundingEventsTable";
import { StatCard } from "@/components/dashboard/StatCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";

export default function PerpsFunding() {
  const { address } = useWallet();
  const { rangeDays, setRangeDays } = useDashboardData({ address, rangeDays: 30, marketType: 'perps' });
  const { data, isLoading, error, refresh } = usePerpsFunding(address, rangeDays);

  const clientSideTotal = data?.events?.reduce((sum, e) => {
    const v = Number(e?.funding ?? e?.usdc ?? e?.amount ?? e?.delta?.usdc ?? e?.delta ?? 0);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0) ?? 0;

  // Always match the table by summing the same funding values shown in events.
  const displayTotal = clientSideTotal;

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!data) return;

    const preview = (data?.events || []).slice(0, 5).map((e: any) => ({
      coin: e?.coin ?? e?.asset ?? e?.symbol ?? '-',
      funding: e?.funding,
      usdc: e?.usdc,
      amount: e?.amount,
      delta_usdc: e?.delta?.usdc,
      delta: e?.delta,
    }));

    console.log('[PerpsFunding] hl-perps-funding response', data);
    console.log('[PerpsFunding] totals', {
      serverTotalFunding: data?.totals?.totalFunding,
      clientSideTotal,
      displayTotal,
      eventsCount: data?.events?.length ?? 0,
      preview,
    });
  }, [data, clientSideTotal, displayTotal]);

  return (
    <main className="container px-4 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Funding</h2>
        <div className="flex items-center gap-2">
          <button
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={refresh}
            disabled={isLoading}
          >
            Refresh
          </button>
          <select
            className="border border-border bg-background rounded-md px-2 py-1 text-sm"
            value={rangeDays}
            onChange={(e) => setRangeDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>All time</option>
          </select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Funding"
          value={displayTotal == null ? "N/A" : `${displayTotal >= 0 ? "+" : ""}$${Number(displayTotal).toFixed(2)}`}
          loading={isLoading}
          tooltip="Net funding paid/received across all perps positions over the selected date range. Positive means you received funding; negative means you paid."
        />
        <StatCard
          title="Days"
          value={String(rangeDays)}
          loading={isLoading}
          tooltip="Number of days included in this funding analysis window."
        />
        <StatCard
          title="Events"
          value={String(data?.events?.length ?? 0)}
          loading={isLoading}
          tooltip="Count of individual funding payment events in the selected date range."
        />
      </div>

      <FundingChart data={data?.daily} loading={isLoading} />

      {data?.events && data.events.length > 0 ? (
        <FundingEventsTable events={data.events} />
      ) : (
        <div className="text-sm text-muted-foreground">No funding events found in this range.</div>
      )}
    </main>
  );
}
